import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Camera, Film, Eye, ShoppingCart, Star, Zap, Heart, Check,
  ChevronRight, ChevronLeft, Shield, Package, Aperture, Battery, Smartphone, Palette
} from 'lucide-react'
import { useState } from 'react'

const AFFILIATE_LINK = 'https://www.amazon.com/dp/B0G834TQB4?pd_rd_i=B0G834TQB4&pd_rd_w=ZULVk&content-id=amzn1.sym.8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_p=8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_r=EG9FB0JEPRQXSZC37AJ1&pd_rd_wg=TKxVO&pd_rd_r=cbf9241b-7219-465d-b4d7-5d61c38d22fa&sp_csd=d2lkZ2V0TmFtZT1zcF9kZXRhaWw&th=1&linkCode=ll1&tag=homeinhouse-20&linkId=0736188751f315773494f5e91f97c981&language=en_US&ref_=as_li_ss_tl'

const getAffiliateLink = (asin) => `https://www.amazon.com/dp/${asin}?linkCode=ll1&tag=homeinhouse-20&language=en_US&ref_=as_li_ss_tl`

const COLOR_VARIANTS = [
  { asin: 'B0G834TQB4', color: 'Yellow', image: 'https://m.media-amazon.com/images/I/71MVqVbjsCL._AC_SL1500_.jpg', hex: '#FFD700' },
  { asin: 'B0GD8BMLL3', color: 'Blue', image: 'https://m.media-amazon.com/images/I/71QN75CjviL._AC_SL1500_.jpg', hex: '#4A90D9' },
  { asin: 'B0GD8C15QN', color: 'Red', image: 'https://m.media-amazon.com/images/I/619sGS27ghL._AC_SL1500_.jpg', hex: '#DC3545' },
  { asin: 'B0GD8DNL3T', color: 'Black', image: 'https://m.media-amazon.com/images/I/71hfjWi9HIL._AC_SL1500_.jpg', hex: '#2D2D2D' },
  { asin: 'B0GD8GHKPS', color: 'White', image: 'https://m.media-amazon.com/images/I/61yFG8z1cZL._AC_SL1500_.jpg', hex: '#F5F5F5' },
  { asin: 'B0GD8HZ4ZD', color: 'Yellow Green', image: 'https://m.media-amazon.com/images/I/71p-PII2wDL._AC_SL1500_.jpg', hex: '#9ACD32' },
  { asin: 'B0GD8JVDTJ', color: 'Clear', image: 'https://m.media-amazon.com/images/I/618v+4yX2wL._AC_SL1500_.jpg', hex: '#E8E8E8' },
  { asin: 'B0GD8LH775', color: 'Grey', image: 'https://m.media-amazon.com/images/I/61FSamg9soL._AC_SL1500_.jpg', hex: '#808080' },
  { asin: 'B0GD8RS6YP', color: 'Green', image: 'https://m.media-amazon.com/images/I/71eCV3ZvoOL._AC_SL1500_.jpg', hex: '#228B22' }
]

const GALLERY_IMAGES = [
  'https://m.media-amazon.com/images/I/81FfgK4zRvL._AC_SL1500_.jpg',
  'https://m.media-amazon.com/images/I/81q1159V3aL._AC_SL1500_.jpg',
  'https://m.media-amazon.com/images/I/81rlWxZvGcL._AC_SL1500_.jpg',
  'https://m.media-amazon.com/images/I/81Kwpk4MXpL._AC_SL1500_.jpg'
]

export default function G6ThumbCameraPage() {
  const [selectedColor, setSelectedColor] = useState(COLOR_VARIANTS[0])
  const [galleryIndex, setGalleryIndex] = useState(0)

  return (
    <>
      <Helmet>
        <title>G6 Thumb Camera - 26g Pocket Digital Camera with CCD Sensor | G6 Optics</title>
        <meta name="description" content="Buy the G6 thumb camera: a 26g keychain camera with CCD sensor for authentic film point and shoot style photos. Available in 9 colors. The ultimate g6 mini camera for retro photography." />
        <link rel="canonical" href="https://g6optics.com/g6-thumb-camera" />
        <meta property="og:title" content="G6 Thumb Camera - Mini Digital Keychain Camera" />
        <meta property="og:description" content="26 gram thumb camera with CCD sensor. Film point and shoot style retro digital photography in your pocket." />
      </Helmet>

      {/* Hero Section */}
      <section className="camera-page pixel-texture">
        <div className="container">
          <motion.div
            className="camera-hero"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="hero-badge retro-border-sm"><Star size={12} /> G6 THUMB CAMERA</span>
            <h1 className="camera-page-title">
              G6 Thumb Camera: The Ultimate <span className="hero-title-accent">Pocket Digital Camera</span>
            </h1>
            <p className="camera-page-desc">
              The <strong>G6 thumb camera</strong> is a revolutionary 26-gram <Link to="/mini-digital-camera">mini digital camera</Link> that 
              delivers authentic <Link to="/retro-mini-camera">film point and shoot</Link> style photography. This tiny digital camera 
              features a genuine CCD sensor for that unmistakable vintage look.
            </p>
          </motion.div>

          {/* Product Showcase */}
          <div className="camera-showcase">
            <motion.div
              className="camera-main-image"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="camera-image-wrapper retro-border">
                <img 
                  src={selectedColor.image} 
                  alt={`G6 Thumb Camera in ${selectedColor.color} - mini digital camera`}
                  width="500"
                  height="500"
                />
              </div>
              <div className="camera-gallery-nav">
                <h3 className="gallery-heading">Sample pictures taken with G6 thumb camera</h3>
                <button
                  className="gallery-nav-btn retro-border-sm"
                  onClick={() => setGalleryIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length)}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="gallery-thumbnails">
                  {GALLERY_IMAGES.map((img, i) => (
                    <div
                      key={i}
                      className={`gallery-thumb ${galleryIndex === i ? 'active' : ''}`}
                      onClick={() => setGalleryIndex(i)}
                    >
                      <img src={img} alt={`G6 thumb camera product view ${i + 1}`} />
                    </div>
                  ))}
                </div>
                <button
                  className="gallery-nav-btn retro-border-sm"
                  onClick={() => setGalleryIndex((prev) => (prev + 1) % GALLERY_IMAGES.length)}
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="gallery-main-image retro-border">
                <img src={GALLERY_IMAGES[galleryIndex]} alt="Sample pictures taken with G6 thumb camera" />
              </div>
            </motion.div>

            <motion.div
              className="camera-details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="camera-rating">
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="star" />
                  ))}
                </div>
                <span>4.8 out of 5 • 2,400+ reviews</span>
              </div>

              <h2 className="camera-name">G6 Thumb Camera — {selectedColor.color}</h2>
              
              <div className="camera-price">
                <span className="price-current">$39.99</span>
                <span className="price-original">$59.99</span>
                <span className="price-badge retro-border-sm">33% OFF</span>
              </div>

              <div className="color-selector">
                <h3>Choose Your G6 Mini Camera Color</h3>
                <div className="color-options">
                  {COLOR_VARIANTS.map((variant) => (
                    <button
                      key={variant.asin}
                      className={`color-option ${selectedColor.asin === variant.asin ? 'selected' : ''}`}
                      style={{ backgroundColor: variant.hex }}
                      onClick={() => setSelectedColor(variant)}
                      title={variant.color}
                      aria-label={`Select ${variant.color} G6 thumb camera`}
                    >
                      {selectedColor.asin === variant.asin && <Check size={14} color={variant.color === 'Black' ? '#fff' : '#000'} />}
                    </button>
                  ))}
                </div>
                <span className="color-name">{selectedColor.color}</span>
              </div>

              <a
                href={getAffiliateLink(selectedColor.asin)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large retro-border"
              >
                <ShoppingCart size={20} />
                Buy G6 Thumb Camera on Amazon
                <ChevronRight size={20} />
              </a>

              <div className="camera-trust-badges">
                <div className="trust-badge">
                  <Shield size={16} />
                  <span>30-Day Returns</span>
                </div>
                <div className="trust-badge">
                  <Package size={16} />
                  <span>Free Shipping</span>
                </div>
                <div className="trust-badge">
                  <Check size={16} />
                  <span>1-Year Warranty</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Detailed Specs */}
          <motion.div
            className="detailed-specs"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">G6 Thumb Camera Technical Specifications</h2>
            
            <div className="specs-table-wrapper retro-border">
              <div className="specs-category">
                <h3><Camera size={18} /> Camera Specs</h3>
                <div className="specs-row">
                  <span>Sensor Type</span>
                  <span>1/1.7" CCD Sensor</span>
                </div>
                <div className="specs-row">
                  <span>Video Resolution</span>
                  <span>1080p Full HD @ 30fps</span>
                </div>
                <div className="specs-row">
                  <span>Photo Resolution</span>
                  <span>12 Megapixels</span>
                </div>
                <div className="specs-row">
                  <span>Field of View</span>
                  <span>130° Ultra Wide Angle</span>
                </div>
                <div className="specs-row">
                  <span>Aperture</span>
                  <span>f/2.0</span>
                </div>
              </div>

              <div className="specs-category">
                <h3><Palette size={18} /> Color Modes</h3>
                <div className="specs-row">
                  <span>Color Profiles</span>
                  <span>8 Built-in Vintage Modes</span>
                </div>
                <div className="specs-row">
                  <span>Available Modes</span>
                  <span>Classic Film, Y2K, Noir, Summer Haze</span>
                </div>
                <div className="specs-row">
                  <span>White Balance</span>
                  <span>Auto / Manual</span>
                </div>
              </div>

              <div className="specs-category">
                <h3><Battery size={18} /> Power & Storage</h3>
                <div className="specs-row">
                  <span>Battery Life</span>
                  <span>200+ Photos / 60 min Video</span>
                </div>
                <div className="specs-row">
                  <span>Charging</span>
                  <span>USB-C Fast Charging</span>
                </div>
                <div className="specs-row">
                  <span>Charge Time</span>
                  <span>~45 minutes</span>
                </div>
                <div className="specs-row">
                  <span>Storage</span>
                  <span>MicroSD up to 128GB</span>
                </div>
              </div>

              <div className="specs-category">
                <h3><Smartphone size={18} /> Physical</h3>
                <div className="specs-row">
                  <span>Weight</span>
                  <span>26 grams (0.9 oz)</span>
                </div>
                <div className="specs-row">
                  <span>Dimensions</span>
                  <span>45 × 35 × 25 mm</span>
                </div>
                <div className="specs-row">
                  <span>Material</span>
                  <span>ABS Plastic + Metal Clip</span>
                </div>
                <div className="specs-row">
                  <span>Mount</span>
                  <span>Keychain Clip / Magnetic</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* SEO Content Section */}
          <section className="seo-content-section">
            <h2 className="section-title">Why the G6 Thumb Camera is the Best Mini Digital Camera</h2>
            
            <div className="seo-content-grid">
              <article className="seo-content-block">
                <h3>What Makes the G6 Thumb Camera Special?</h3>
                <p>
                  The <strong>G6 thumb camera</strong> represents a new category in photography: the ultra-portable 
                  <em> keychain camera</em>. Unlike bulky DSLRs or even compact cameras, the G6 weighs just 26 grams—lighter 
                  than most car keys. This <Link to="/mini-digital-camera">tiny digital camera</Link> clips directly onto your 
                  keychain, bag strap, or lanyard, ensuring you never miss a photo opportunity.
                </p>
                <p>
                  What truly sets the G6 apart from other <em>pocket digital cameras</em> is its authentic CCD sensor. 
                  While most modern cameras use CMOS sensors optimized for clinical sharpness, the G6's CCD sensor 
                  produces images with natural grain, warm color science, and that coveted <Link to="/retro-mini-camera">film 
                  point and shoot</Link> aesthetic that photographers love.
                </p>
              </article>

              <article className="seo-content-block">
                <h3>G6 Camera vs. Traditional Mini Digital Cameras</h3>
                <p>
                  Traditional <strong>mini digital cameras</strong> focus on cramming smartphone-like features into a smaller body. 
                  The <em>G6 digital camera</em> takes a different approach: it prioritizes character over specifications. 
                  The result is a <em>toy camera</em> that produces professional-quality vintage aesthetics without the 
                  bulk of traditional gear.
                </p>
                <p>
                  For photographers seeking a <Link to="/retro-mini-camera">vintage digital camera</Link> experience, 
                  the G6 delivers what software filters cannot: authentic analog character baked into every shot. 
                  This isn't a <em>disposable digital camera</em> clone—it's a purpose-built tool for modern 
                  retro photography enthusiasts.
                </p>
              </article>
            </div>
          </section>

          {/* All Colors Grid */}
          <motion.div
            className="all-colors-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">G6 Thumb Camera - Available in 9 Colors</h2>
            <p className="section-desc">Express yourself with your choice of G6 mini camera. Each color ships separately with Prime delivery.</p>
            
            <div className="colors-grid">
              {COLOR_VARIANTS.map((variant, i) => (
                <motion.div
                  key={variant.asin}
                  className="color-card retro-border-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <div className="color-card-image">
                    <img src={variant.image} alt={`G6 Thumb Camera in ${variant.color}`} loading="lazy" />
                  </div>
                  <div className="color-card-info">
                    <span className="color-card-name">{variant.color}</span>
                    <a
                      href={getAffiliateLink(variant.asin)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="color-card-btn retro-border-sm"
                    >
                      Buy {variant.color}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Internal Links Section */}
          <section className="internal-links-section">
            <h2 className="section-title">Explore More</h2>
            <div className="internal-links-grid">
              <Link to="/retro-mini-camera" className="internal-link-card retro-border-sm">
                <h3>Retro Camera Style Guide</h3>
                <p>Learn why the G6 delivers authentic early 2000s digital camera aesthetics</p>
              </Link>
              <Link to="/mini-digital-camera" className="internal-link-card retro-border-sm">
                <h3>Mini Digital Camera Comparison</h3>
                <p>See how the G6 compares to other pocket digital cameras</p>
              </Link>
              <Link to="/faq" className="internal-link-card retro-border-sm">
                <h3>Frequently Asked Questions</h3>
                <p>Common questions about the G6 thumb camera answered</p>
              </Link>
            </div>
          </section>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta pixel-texture">
        <div className="container">
          <motion.div
            className="cta-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">
              Get Your <span className="cta-title-accent">G6 Thumb Camera</span> Today
            </h2>
            <p className="cta-desc">
              Join thousands of photographers who've discovered the joy of authentic retro photography 
              with the G6 mini digital camera.
            </p>
            <a
              href={getAffiliateLink(selectedColor.asin)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary retro-border"
            >
              <ShoppingCart size={18} />
              Buy G6 Thumb Camera in {selectedColor.color}
              <ChevronRight size={18} />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  )
}
